/**
 * Zoom at which the outside-boundary-points feature hands off between its two layers:
 * the precomputed cluster grid at or below this zoom, individual points above it.
 *
 * **This is the only thing that decides which layer draws.** The SLDs do not share the
 * decision - the grid style has no scale rules at all, and the raw style's
 * MaxScaleDenominator (100,000) is a deliberately loose guard set well clear of this
 * boundary, so the two styles' scale ranges overlap and the switch here is unambiguous.
 *
 * Layers are paired as `maxZoom: CLUSTER_MAX_ZOOM` on the grid and
 * `minZoom: CLUSTER_MAX_ZOOM` on the raw points. ol treats minZoom as exclusive and
 * maxZoom as inclusive, so those meet exactly: grid at or below this zoom, raw above it,
 * with no gap or overlap at any fractional zoom.
 *
 * Raised from 13 to 14 because the raw layer took over 4 seconds at zoom 14 on a dense
 * tenant. The viewport is ~13km wide there, so individual 6px points are slow to render.
 *
 * Changing this means changing three things together:
 *   1. this constant;
 *   2. `p_zoom_max` in vector_products.refresh_outside_boundary_points_grid, or the grid
 *      has no rows at the new level and it renders blank;
 *   3. `MaxScaleDenominator` in deployment/geoserver/outside_boundary_points.sld, which
 *      must stay comfortably above the new boundary's scale denominator
 *      (559082264 / 2^zoom) or the raw layer falls out of scale just above the handoff.
 * Every existing tenant also needs its grid refreshed before the new level has data.
 * (call `vector_products.refresh_outside_boundary_points_grid for each existing` tenant)
 */
export const CLUSTER_MAX_ZOOM = 14
export const GRID_MIN_ZOOM = 6

/**
 * Pick the precomputed grid level to request for a map zoom.
 *
 * Rounded and clamped, because the grid only exists at whole levels between GRID_MIN_ZOOM
 * and CLUSTER_MAX_ZOOM.
 */
export function gridZoomFor(zoom: number | undefined): number {
  return Math.min(
    CLUSTER_MAX_ZOOM,
    Math.max(GRID_MIN_ZOOM, Math.round(zoom ?? 0)),
  )
}

/**
 * Build the WMS `viewparams` string for either layer.
 *
 * The shape is load-bearing in two places beyond this app, so don't reformat it casually:
 * each value has to satisfy the GeoServer SQL view's `regexpValidator` (which is the only
 * SQL-injection defence, because the substitution is textual), and the same string is
 * matched by the GeoWebCache parameter filter that keeps one tenant's tiles away from
 * another's. See deployment/geoserver/outside_boundary_points_grid.md.
 */
export function buildViewparams(
  tenantId: string,
  year: number | undefined,
  gridZoom?: number,
): string {
  const base = `tenant_id:${tenantId};year:${year ?? ''}`
  return gridZoom === undefined ? base : `${base};z:${gridZoom}`
}
